import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { apiFetch, apiPost } from "./api/client";
import { UI } from "./designSystem";
import { getEffectiveRoles } from "./roleSimulator";

// Voltar dessas telas de administração deve ir pro painel de admin (/admin),
// não pro site.
const ADMIN_PATH = "/admin";

// Rotulo do status de um cadastro de membro INATIVO/TRANSFERIDO/FALECIDO/
// PENDENTE reclassificado como "Outros com acesso" (ver isAppOnly no
// backend, /admin/care-directory) -- so' pra diferenciar, na legenda, de
// uma conta avulsa de verdade (que nunca teve cadastro nenhum). Mesmos
// rotulos usados em AdminMembros.jsx.
const INACTIVE_MEMBER_STATUS_LABEL = {
  INATIVO: "inativo",
  TRANSFERIDO: "transferido",
  FALECIDO: "falecido",
  PENDENTE: "pendente"
};

const pageStyle = {
  minHeight: "100vh",
  width: "100%",
  boxSizing: "border-box",
  backgroundColor: UI.shell,
  color: UI.text,
  fontFamily: "'DM Sans', system-ui, sans-serif",
  padding: "28px 20px 60px"
};

const backLinkStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  color: "#9fb0cc",
  fontSize: 13,
  fontWeight: 700,
  textDecoration: "none",
  marginBottom: 18
};

const cardStyle = {
  background: "linear-gradient(160deg, #0f1727, #131f34)",
  border: "1px solid rgba(120,152,210,0.28)",
  borderRadius: 16,
  padding: 18,
  boxShadow: "0 14px 28px rgba(0,0,0,0.36), inset 0 1px 0 rgba(255,255,255,0.06)"
};

const fieldLabelStyle = { display: "block", margin: "0 0 7px", color: "#cbd5e1", fontSize: 11, letterSpacing: 0.6, textTransform: "uppercase", fontWeight: 800 };

const inputStyle = {
  width: "100%",
  border: "1px solid rgba(226,232,240,0.16)",
  background: "rgba(12,20,34,0.68)",
  color: "#F8FAFC",
  borderRadius: 14,
  padding: "12px 13px",
  fontSize: 14,
  fontFamily: "inherit",
  boxSizing: "border-box",
  outline: "none",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)"
};

// Sem contraparte em AdminMembros/AdminCriancas/AdminEventos -- os botões
// desta tela vêm com estilo próprio (sem o padrão do navegador), do mesmo
// jeito que existiam em PrototypeApp.js.
const buttonReset = { background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", color: "inherit", padding: 0 };

function CenteredMessage({ children }) {
  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 480, margin: "18vh auto 0", textAlign: "center" }}>
        <p style={{ margin: 0, color: "#c7ced9", fontSize: 14, lineHeight: 1.6 }}>{children}</p>
        <Link to={ADMIN_PATH} style={{ ...backLinkStyle, marginTop: 18, justifyContent: "center" }}>
          <ArrowLeft size={14} /> Voltar
        </Link>
      </div>
    </div>
  );
}

const MEMBER_INVITE_TEMPLATES = {
  CAFE: (member) => `Oi, ${(member.fullName || "").split(" ")[0]}! Aqui é da Igreja Batista Mosaico. Que tal marcarmos um café pra gente conversar com calma? Me conta se topa.`,
  SIGNUP: (member) => `Oi, ${(member.fullName || "").split(" ")[0]}! Aqui é da Igreja Batista Mosaico. Você já pode acessar nosso app pra devocionais, agenda e muito mais -- é só se cadastrar por aqui: https://batistamosaico.com.br/entrar`,
  DEVOTIONAL: (member) => `Oi, ${(member.fullName || "").split(" ")[0]}! Aqui é da Igreja Batista Mosaico. Que tal a gente fazer um devocional juntos, pra caminhar mais de perto? Me conta se topa.`
};
const MEMBER_INVITE_LABELS = { CAFE: "Café", SIGNUP: "Cadastro no site", DEVOTIONAL: "Devocional" };

export default function AdminAcolhimento() {
  const [status, setStatus] = useState("checking");
  const [viewerUser, setViewerUser] = useState(null);
  const [isTablet, setIsTablet] = useState(typeof window !== "undefined" ? window.innerWidth >= 768 : true);

  const [adminOverview, setAdminOverview] = useState(null);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminFeedback, setAdminFeedback] = useState("");
  const [careDirectory, setCareDirectory] = useState([]);
  const [careLoading, setCareLoading] = useState(false);
  const [careFeedback, setCareFeedback] = useState("");
  const [careFilter, setCareFilter] = useState("");
  // "all" | "memberWithAccess" | "memberWithoutAccess" | "otherWithAccess" --
  // filtro combinando e-membro-oficial x tem-conta-no-app, alem da busca
  // por texto (ver filteredCareMembers).
  const [careAccessFilter, setCareAccessFilter] = useState("all");
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [memberInviteFeedback, setMemberInviteFeedback] = useState("");
  const [sendingInviteType, setSendingInviteType] = useState("");
  // "all" | "inactive7d" -- ligado ao clique no indicador "Ativos no
  // devocional (7d)", pra reenquadrar a lista de membros na pergunta mais
  // acionavel (quem NAO fez devocional na semana, em vez de so contar quem fez).
  const [careActivityFilter, setCareActivityFilter] = useState("all");
  const careSectionRef = useRef(null);
  const bookingsSectionRef = useRef(null);
  const trailInvitesSectionRef = useRef(null);
  // Foco automatico no campo de busca ao entrar na tela -- pedido do
  // Rodrigo: o uso real desta tela e' quase sempre "abrir e buscar uma
  // pessoa", nao navegar pelos indicadores primeiro.
  const careFilterInputRef = useRef(null);
  const scrollToSection = (ref) => {
    if (ref?.current) ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    const handleResize = () => setIsTablet(window.innerWidth >= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    let alive = true;
    apiFetch("/auth/me", { method: "GET", skipSessionExpiredHandler: true })
      .then((me) => {
        if (!alive) return;
        const canAccess = getEffectiveRoles(me).includes("ADMIN");
        if (canAccess && !me.passwordMustChange) {
          setViewerUser(me);
          setStatus("ready");
        } else {
          setStatus("forbidden");
        }
      })
      .catch(() => {
        if (alive) setStatus("forbidden");
      });
    return () => {
      alive = false;
    };
  }, []);

  const canManage = status === "ready" && Boolean(viewerUser?.id);

  useEffect(() => {
    if (canManage) careFilterInputRef.current?.focus();
  }, [canManage]);

  // Fetch proprio desta tela (nao reaproveita o loadAdminOverview de
  // PrototypeApp.js, que tambem busca /pastoral/slots -- aqui so precisamos
  // dos indicadores/busca, o resto ficou na Agenda de cuidado).
  const loadAdminOverview = async () => {
    if (!canManage) return;
    setAdminLoading(true);
    setAdminFeedback("");
    try {
      const overview = await apiFetch("/admin/overview", { method: "GET", viewer: viewerUser });
      setAdminOverview(overview);
    } catch (error) {
      setAdminFeedback("Não foi possível carregar o painel de cuidado agora.");
    } finally {
      setAdminLoading(false);
    }
  };

  // Lista de membros pronta na tela (sem precisar digitar pra buscar), com
  // historico de devocionais/agendamentos de quem ja tem conta no app --
  // mesmo padrao do filtro+lista do Historico em /admin/visitantes.
  const loadCareDirectory = async () => {
    if (!canManage) return;
    setCareLoading(true);
    setCareFeedback("");
    try {
      const data = await apiFetch("/admin/care-directory", { method: "GET", viewer: viewerUser });
      setCareDirectory(data?.members || []);
    } catch (error) {
      setCareFeedback("Não foi possível carregar a lista de membros agora.");
    } finally {
      setCareLoading(false);
    }
  };

  useEffect(() => {
    if (canManage) {
      loadAdminOverview();
      loadCareDirectory();
    }
  }, [canManage]);

  const careFilterTerm = careFilter.trim().toLowerCase();
  const careTextFiltered = careFilterTerm
    ? careDirectory.filter((member) => (
        (member.fullName || "").toLowerCase().includes(careFilterTerm) ||
        (member.city || "").toLowerCase().includes(careFilterTerm) ||
        (member.email || "").toLowerCase().includes(careFilterTerm)
      ))
    : careDirectory;
  const careAccessFiltered = careAccessFilter === "all"
    ? careTextFiltered
    : careAccessFilter === "otherWithAccess"
      ? careTextFiltered.filter((member) => member.isAppOnly)
      : careAccessFilter === "memberWithAccess"
        ? careTextFiltered.filter((member) => !member.isAppOnly && member.hasAccess)
        : careTextFiltered.filter((member) => !member.isAppOnly && !member.hasAccess);
  // Ativo no devocional (7d) = concluiu ao menos um devocional nos ultimos
  // 7 dias -- mesma janela do indicador "Ativos no devocional (7d)" do topo,
  // calculada aqui em cima do historico que ja vem no /admin/care-directory.
  const isActiveThisWeek = (member) => {
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return (member.devotionals || []).some((item) => item.doneAt && new Date(item.doneAt).getTime() >= sevenDaysAgo);
  };
  // Selo de engajamento (devocional OU ebook, via lastActivityAt que ja vem
  // calculado do /admin/care-directory) -- substitui o selo de cadastro de
  // membro como destaque principal da lista: o pedido do Rodrigo foi "essa
  // tela deveria ser sobre engajamento digital", nao sobre status de cadastro
  // (isso ja mora em outra tela). O status de cadastro continua visivel, so
  // que como legenda secundaria (ver uso de activityChip abaixo).
  const activityChip = (member) => {
    if (!member.isAppOnly && !member.hasAccess) {
      // Nunca criou conta no app -- "sem atividade" aqui seria redundante com a
      // legenda de cadastro (que ja diz isso) e sugere falsamente que a pessoa
      // teve a chance de engajar e nao quis. Selo proprio, mais neutro.
      return { label: "Sem acesso ao app", color: "#8b96a8", border: "rgba(148,163,184,0.22)", background: "rgba(148,163,184,0.06)" };
    }
    if (!member.lastActivityAt) {
      return { label: "Sem atividade", color: "#c7ced9", border: "rgba(148,163,184,0.3)", background: "rgba(148,163,184,0.1)" };
    }
    const days = Math.floor((Date.now() - new Date(member.lastActivityAt).getTime()) / (24 * 60 * 60 * 1000));
    if (days <= 7) {
      return { label: days <= 0 ? "Ativo hoje" : `Ativo há ${days}d`, color: "#86EFAC", border: "rgba(74,222,128,0.4)", background: "rgba(34,197,94,0.14)" };
    }
    return { label: `Inativo há ${days}d`, color: "#FDE68A", border: "rgba(250,204,21,0.35)", background: "rgba(250,204,21,0.1)" };
  };
  const filteredCareMembers = careActivityFilter === "inactive7d"
    ? careAccessFiltered.filter((member) => member.hasAccess && !isActiveThisWeek(member))
    : careAccessFiltered;
  // Quatro grupos possiveis (pedido do Rodrigo: separar o eixo "e membro
  // oficial?" do eixo "tem conta no app?" em vez de misturar os dois num
  // "sem cadastro de membro" so). "Outros sem acesso" nao existe -- pra
  // aparecer nesta lista a pessoa precisa ter conta (membro com conta, ou
  // conta avulsa), entao toda conta avulsa por definicao tem acesso.
  const careMembersWithAccessCount = careDirectory.filter((member) => !member.isAppOnly && member.hasAccess).length;
  const careMembersWithoutAccessCount = careDirectory.filter((member) => !member.isAppOnly && !member.hasAccess).length;
  const careOtherWithAccessCount = careDirectory.filter((member) => member.isAppOnly).length;
  const selectedCareMember = careDirectory.find((member) => member.id === selectedMemberId) || null;
  // Resumo de conversas de acolhimento (pills de contagem), nao lista
  // detalhada -- pedido do Rodrigo: essa tela e' sobre buscar uma pessoa,
  // a lista completa de conversas mora na Agenda de cuidado (link abaixo).
  const recentBookingsList = adminOverview?.recentBookings || [];
  const pendingBookingsInRecent = recentBookingsList.filter((booking) => booking.status === "PENDING").length;
  const confirmedBookingsInRecent = recentBookingsList.filter((booking) => booking.status === "CONFIRMED").length;

  // Mostra quando o mesmo tipo de convite ja foi mandado antes, pra nao
  // reenviar sem notar -- pedido do Rodrigo depois da sugestao de UX
  // (risco real: mandar convite duplicado pra mesma pessoa).
  const lastInviteOf = (member, type) => (member?.invites || []).find((item) => item.type === type) || null;

  const sendMemberInvite = async (type) => {
    if (!selectedCareMember || !canManage) return;
    const phoneDigits = (selectedCareMember.mobilePhone || "").replace(/\D/g, "");
    if (!phoneDigits) {
      setMemberInviteFeedback("Essa pessoa não tem WhatsApp cadastrado.");
      return;
    }
    setMemberInviteFeedback("");
    setSendingInviteType(type);
    try {
      const invite = await apiPost(`/admin/members/${encodeURIComponent(selectedCareMember.id)}/invites`, {
        viewer: viewerUser,
        body: { type }
      });
      setCareDirectory((prev) =>
        prev.map((member) =>
          member.id === selectedCareMember.id
            ? { ...member, invites: [invite, ...(member.invites || [])].slice(0, 10) }
            : member
        )
      );
      setMemberInviteFeedback("Convite registrado -- confira o WhatsApp que abriu pra enviar.");
      const message = MEMBER_INVITE_TEMPLATES[type](selectedCareMember);
      window.open(`https://wa.me/55${phoneDigits.replace(/^55/, "")}?text=${encodeURIComponent(message)}`, "_blank");
    } catch (error) {
      setMemberInviteFeedback("Não foi possível registrar o convite agora.");
    } finally {
      setSendingInviteType("");
    }
  };

  if (status === "checking") {
    return <CenteredMessage>Carregando...</CenteredMessage>;
  }

  if (status === "forbidden") {
    return <CenteredMessage>Esta página é restrita à equipe administrativa. Entre com uma conta autorizada para acessar o painel de acolhimento.</CenteredMessage>;
  }

  // Indicadores agrupados em 2 blocos por prioridade (nao mais por origem
  // do dado): "Precisa de atencao" junta as 2 pendencias com o indicador de
  // engajamento mais sensivel (ativos no devocional), e "Visao geral" junta
  // o resto. Antes eram 3 grupos (Engajamento / Pendencias / Cadastro)
  // separados por categoria, o que deixava um numero preocupante (ex.: "0
  // ativos") com o mesmo peso visual de um dado neutro.
  // Seta de tendencia (sobe/desce/igual) comparando com a mesma contagem na
  // janela anterior (metrics.*Previous, calculado no backend) -- pedido da
  // revisao de UX: numero cru de "Ativos (7d)" nao dizia se e queda ou
  // estabilidade. So' aparece quando os dois lados existem (adminOverview
  // ja carregado); simbolo (nao so cor) garante que funciona sem depender
  // de enxergar a cor.
  const trendOf = (current, previous) => {
    if (typeof current !== "number" || typeof previous !== "number") return null;
    const delta = current - previous;
    if (delta > 0) return { symbol: "↑", color: "#86EFAC", label: `${delta > 0 ? "+" : ""}${delta} vs. semana anterior (${previous})` };
    if (delta < 0) return { symbol: "↓", color: "#FCA5A5", label: `${delta} vs. semana anterior (${previous})` };
    return { symbol: "=", color: "#8fb5cb", label: `igual à semana anterior (${previous})` };
  };
  const PENDING_TILES = [
    { key: "pendingInvites", label: "Convites de trilha pendentes", value: adminOverview?.metrics?.pendingInvites ?? "-", tier: "acao", onClick: () => scrollToSection(trailInvitesSectionRef) },
    { key: "pendingBookings", label: "Conversas pendentes", value: adminOverview?.metrics?.pendingBookings ?? "-", tier: "acao", onClick: () => scrollToSection(bookingsSectionRef) }
  ];
  // Revisao de UX (10/2026, a pedido do Rodrigo -- "nao gosto dessa tela,
  // pode sugerir outra proposta de layout?"): o numero mais preocupante da
  // tela ("Ativos no devocional" caindo ou zerado) tinha o mesmo peso visual
  // de um dado neutro como "Contas no app", e ficava longe das Pendencias,
  // que sao as duas outras coisas que realmente pedem uma acao da lideranca.
  // Agora esse tile entra no mesmo grupo das Pendencias ("Precisa de
  // atencao"), com destaque de cor so quando a tendencia e' de queda.
  const devotionalTile = {
    key: "devotionalActiveWeek",
    label: "Ativos no devocional (7d)",
    value: adminOverview?.metrics?.devotionalActiveWeek ?? "-",
    trend: trendOf(adminOverview?.metrics?.devotionalActiveWeek, adminOverview?.metrics?.devotionalActiveWeekPrevious),
    onClick: () => { setCareActivityFilter("inactive7d"); scrollToSection(careSectionRef); }
  };
  devotionalTile.tier = devotionalTile.trend?.symbol === "↓" ? "alerta" : "geral";
  const ATTENTION_TILES = [...PENDING_TILES, devotionalTile];

  const ebookTile = {
    key: "ebookActiveWeek",
    label: "Leituras de ebook (7d)",
    value: adminOverview?.metrics?.ebookActiveWeek ?? "-",
    trend: trendOf(adminOverview?.metrics?.ebookActiveWeek, adminOverview?.metrics?.ebookActiveWeekPrevious),
    tier: "geral",
    onClick: () => scrollToSection(careSectionRef)
  };
  // Antes "Contas no app" e "Perfis completos" eram 2 tiles separados
  // mostrando o mesmo numero (14/14) -- confuso e redundante. Agora e' um
  // tile so', com a segunda linha mostrando se falta alguem completar o
  // perfil. (Numeros nao batem com a lista "Pessoas e engajamento" logo
  // abaixo por design -- universos diferentes, duvida antiga do Rodrigo:
  // "mostra 14 aqui, mas mais abaixo diz apenas 6".)
  const accountsUsers = adminOverview?.metrics?.users;
  const accountsCompleted = adminOverview?.metrics?.completedProfiles;
  const accountsHasBoth = typeof accountsUsers === "number" && typeof accountsCompleted === "number";
  const accountsAllComplete = accountsHasBoth && accountsCompleted >= accountsUsers;
  const accountsTile = {
    key: "accountsOverview",
    label: "Contas no app",
    value: accountsUsers ?? "-",
    sub: accountsHasBoth ? (accountsAllComplete ? "Todos os perfis completos" : `${accountsCompleted} de ${accountsUsers} perfis completos`) : null,
    subOk: accountsAllComplete,
    tier: "geral",
    onClick: () => scrollToSection(careSectionRef)
  };
  const OVERVIEW_TILES = [ebookTile, accountsTile];

  const renderTile = (tile) => {
    const isAcao = tile.tier === "acao";
    const isHighlighted = isAcao || tile.tier === "alerta";
    const isClickable = Boolean(tile.onClick);
    const Wrapper = isClickable ? "button" : "div";
    return (
      <Wrapper
        key={tile.key}
        {...(isClickable ? { onClick: tile.onClick, type: "button" } : {})}
        {...(!isClickable && isAcao ? { title: "Ainda sem tela própria pra gerenciar isso por aqui" } : {})}
        style={{
          ...(isClickable ? buttonReset : {}),
          textAlign: "left",
          display: "block",
          width: "100%",
          boxSizing: "border-box",
          padding: "11px 10px",
          border: isHighlighted ? "1px solid rgba(250,204,21,0.4)" : "1px solid rgba(125,211,252,0.20)",
          background: isHighlighted ? "rgba(250,204,21,0.12)" : "rgba(15,23,42,0.46)",
          cursor: isClickable ? "pointer" : "default"
        }}
      >
        <p style={{ margin: "0 0 5px", display: "flex", alignItems: "baseline", gap: 6, color: isHighlighted ? "#FDE68A" : "#E0F2FE", fontSize: 18, fontWeight: 950, fontVariantNumeric: "tabular-nums" }}>
          {tile.value}
          {tile.trend && (
            <span title={tile.trend.label} style={{ fontSize: 12, fontWeight: 800, color: tile.trend.color }}>
              {tile.trend.symbol}
            </span>
          )}
        </p>
        <p style={{ margin: tile.sub ? "0 0 3px" : 0, color: isHighlighted ? "#F8DFA8" : "#8fb5cb", fontSize: 10, textTransform: "uppercase", letterSpacing: 1, fontWeight: 800 }}>
          {tile.label}{isClickable && <span style={{ marginLeft: 5 }}>→</span>}
        </p>
        {tile.sub && (
          <p style={{ margin: 0, color: tile.subOk ? "#86EFAC" : "#8fb5cb", fontSize: 10.5, fontWeight: 700 }}>
            {tile.subOk ? "✓ " : ""}{tile.sub}
          </p>
        )}
      </Wrapper>
    );
  };
  const tileGroupLabelStyle = { margin: "0 0 6px", color: "#8fb5cb", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 800 };
  const tileGroupGridStyle = { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8, marginBottom: 12 };
  const attentionGridStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8, marginBottom: 12, padding: 10, borderRadius: 10, border: "1px solid rgba(250,204,21,0.22)", background: "rgba(250,204,21,0.04)" };

  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 980, margin: "0 auto" }}>
        <Link to={ADMIN_PATH} style={backLinkStyle}>
          <ArrowLeft size={14} /> Voltar
        </Link>

        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 6, flexWrap: "wrap" }}>
          <div>
            <p style={{ margin: "0 0 6px", fontSize: 11, letterSpacing: 1.35, color: "#7DD3FC", textTransform: "uppercase", fontWeight: 900 }}>Painel de cuidado</p>
            <h1 style={{ margin: 0, fontSize: 26, fontWeight: 950, color: "#FFF" }}>Cuidado e engajamento</h1>
          </div>
          <button onClick={() => { loadAdminOverview(); loadCareDirectory(); }} disabled={adminLoading || careLoading} style={{ display: "inline-flex", alignItems: "center", gap: 6, minHeight: 38, padding: "0 14px", border: "1px solid rgba(125,211,252,0.38)", background: "rgba(14,165,233,0.12)", color: "#BAE6FD", fontSize: 12, fontWeight: 900, borderRadius: 8, cursor: (adminLoading || careLoading) ? "default" : "pointer", opacity: (adminLoading || careLoading) ? 0.7 : 1 }}>
            {(adminLoading || careLoading) ? "Atualizando" : "Atualizar"}
          </button>
        </div>
        <p style={{ margin: "0 0 22px", color: "#9fb0cc", fontSize: 13, lineHeight: 1.6, maxWidth: 640 }}>
          Visão restrita para liderança acompanhar engajamento digital (devocional e ebooks), conexão da comunidade, pendências pastorais e cadastro de membros.
        </p>

        {adminFeedback && <p style={{ margin: "0 0 12px", color: "#FCA5A5", fontSize: 12 }}>{adminFeedback}</p>}

        <p style={tileGroupLabelStyle}>Precisa de atenção</p>
        <div style={attentionGridStyle}>
          {ATTENTION_TILES.map(renderTile)}
        </div>

        <p style={tileGroupLabelStyle}>Visão geral</p>
        <div style={{ ...tileGroupGridStyle, marginBottom: 14 }}>
          {OVERVIEW_TILES.map(renderTile)}
        </div>

        {/* Destaque reforcado (borda/fundo mais fortes, titulo maior) --
            esse e o bloco primario da tela (busca de pessoa). O resumo de
            conversas/devocional/ebook ficou numa unica tira compacta
            abaixo ("Resumo"), sem competir visualmente com a busca. */}
        <div ref={careSectionRef} style={{ ...cardStyle, marginBottom: 14, border: "1px solid rgba(125,211,252,0.34)", background: "rgba(255,255,255,0.05)", padding: 12 }}>
          <p style={{ margin: "0 0 4px", color: "#F8FAFC", fontSize: 14, fontWeight: 950 }}>Pessoas e engajamento</p>
          <p style={{ margin: "0 0 9px", color: "#8fb5cb", fontSize: 11, lineHeight: 1.5 }}>Lista ordenada por quem esteve mais ativo recentemente (devocional ou ebook). Selecione alguém pra ver as últimas leituras, devocionais e agendamentos.</p>
          <p style={{ margin: "0 0 9px", color: "#7c8ba3", fontSize: 11, lineHeight: 1.5, fontStyle: "italic" }}>Esta lista junta o cadastro de membresia da igreja com as contas do app que ainda não têm um cadastro de membro vinculado (marcadas como "Conta do app · sem cadastro"). Os filtros abaixo separam por esse eixo de cadastro -- a ordem da lista continua sendo por engajamento, não pelo filtro escolhido.</p>
          {careActivityFilter === "inactive7d" && (
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "7px 10px", marginBottom: 9, border: "1px solid rgba(250,204,21,0.35)", background: "rgba(250,204,21,0.1)" }}>
              <span style={{ color: "#FDE68A", fontSize: 11, fontWeight: 800 }}>Mostrando quem tem acesso mas não concluiu devocional nos últimos 7 dias</span>
              <button onClick={() => setCareActivityFilter("all")} style={{ ...buttonReset, color: "#FDE68A", fontSize: 11, fontWeight: 900, textDecoration: "underline" }}>Limpar filtro</button>
            </div>
          )}
          <label>
            <span style={fieldLabelStyle}>Buscar</span>
            <input ref={careFilterInputRef} value={careFilter} onChange={(event) => setCareFilter(event.target.value)} placeholder="Nome, cidade ou e-mail..." style={inputStyle} />
          </label>
          <p style={{ margin: "10px 0 4px", color: "#7c8ba3", fontSize: 9.5, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 800 }}>Filtrar por cadastro</p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 2 }}>
            {[
              ["all", `Todos (${careDirectory.length})`],
              ["memberWithAccess", `Membros com acesso (${careMembersWithAccessCount})`],
              ["memberWithoutAccess", `Membros sem acesso (${careMembersWithoutAccessCount})`],
              ["otherWithAccess", `Outros com acesso (${careOtherWithAccessCount})`]
            ].map(([value, label]) => {
              const isActive = careAccessFilter === value;
              return (
                <button
                  key={value}
                  onClick={() => setCareAccessFilter(value)}
                  style={{
                    ...buttonReset,
                    padding: "5px 9px",
                    border: isActive ? "1px solid rgba(94,234,212,0.6)" : "1px solid rgba(125,211,252,0.2)",
                    background: isActive ? "rgba(20,184,166,0.16)" : "rgba(15,23,42,0.4)",
                    color: isActive ? "#99F6E4" : "#8fb5cb",
                    fontSize: 9.5,
                    fontWeight: 900,
                    textTransform: "uppercase",
                    letterSpacing: 0.4
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
          {careFeedback && <p style={{ margin: "10px 0 0", color: "#FCA5A5", fontSize: 12 }}>{careFeedback}</p>}
          <div style={{ display: "grid", gap: 6, marginTop: 10, maxHeight: 240, overflowY: "auto", paddingRight: 4 }}>
            {filteredCareMembers.map((member) => {
              const isSelected = selectedCareMember?.id === member.id;
              return (
                <button
                  key={member.id}
                  onClick={() => setSelectedMemberId(member.id)}
                  style={{
                    ...buttonReset,
                    textAlign: "left",
                    padding: "9px 10px",
                    border: isSelected ? "1px solid rgba(94,234,212,0.68)" : "1px solid rgba(125,211,252,0.16)",
                    background: isSelected ? "rgba(20,184,166,0.14)" : "rgba(15,23,42,0.4)",
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "baseline",
                    justifyContent: "space-between",
                    gap: 8
                  }}
                >
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: "block", color: isSelected ? "#99F6E4" : "#F8FAFC", fontSize: 12.5, fontWeight: 900, overflowWrap: "anywhere" }}>{member.fullName}</span>
                    {member.city && <span style={{ display: "block", color: "#8fb5cb", fontSize: 10.5, marginTop: 2 }}>{member.city}</span>}
                    <span style={{ display: "block", color: "#7c8ba3", fontSize: 9.5, marginTop: 2 }}>
                      {member.isAppOnly
                        ? member.status
                          ? `Cadastro de membro · ${INACTIVE_MEMBER_STATUS_LABEL[member.status] || member.status}`
                          : "Conta do app · sem cadastro"
                        : member.hasAccess
                          ? "Cadastro de membro"
                          : "Sem cadastro no app"}
                    </span>
                  </span>
                  <span
                    style={{
                      padding: "3px 8px",
                      border: `1px solid ${activityChip(member).border}`,
                      background: activityChip(member).background,
                      color: activityChip(member).color,
                      fontSize: 9.5,
                      fontWeight: 900,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      whiteSpace: "nowrap"
                    }}
                  >
                    {activityChip(member).label}
                  </span>
                </button>
              );
            })}
            {!filteredCareMembers.length && !careLoading && (
              <p style={{ margin: 0, color: "#7c8ba3", fontSize: 11 }}>
                {careActivityFilter === "inactive7d"
                  ? "Ninguém com acesso ficou de fora -- todo mundo concluiu devocional nos últimos 7 dias."
                  : "Nenhum membro encontrado."}
              </p>
            )}
            {careLoading && !careDirectory.length && <p style={{ margin: 0, color: "#7c8ba3", fontSize: 11 }}>Carregando membros...</p>}
          </div>

          {selectedCareMember && (
            <div style={{ border: "1px solid rgba(125,211,252,0.2)", background: "rgba(15,23,42,0.5)", padding: 12, marginTop: 12 }}>
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
                <p style={{ margin: 0, color: "#F8FAFC", fontSize: 14, fontWeight: 900 }}>{selectedCareMember.fullName}</p>
                <span
                  style={{
                    padding: "3px 8px",
                    border: `1px solid ${activityChip(selectedCareMember).border}`,
                    background: activityChip(selectedCareMember).background,
                    color: activityChip(selectedCareMember).color,
                    fontSize: 10,
                    fontWeight: 900,
                    textTransform: "uppercase",
                    letterSpacing: 0.5
                  }}
                >
                  {activityChip(selectedCareMember).label}
                </span>
              </div>
              <p style={{ margin: "4px 0 0", color: "#9fb0cc", fontSize: 11 }}>
                {[selectedCareMember.email, selectedCareMember.mobilePhone, selectedCareMember.city].filter(Boolean).join(" · ") || "Sem contato registrado"}
              </p>
              <p style={{ margin: "2px 0 0", color: "#7c8ba3", fontSize: 10.5 }}>
                {selectedCareMember.isAppOnly
                  ? selectedCareMember.status
                    ? `Cadastro de membro · ${INACTIVE_MEMBER_STATUS_LABEL[selectedCareMember.status] || selectedCareMember.status} (não conta como membro ativo)`
                    : "Conta do app · sem cadastro de membro"
                  : selectedCareMember.hasAccess
                    ? "Cadastro de membro · com acesso à plataforma"
                    : "Cadastro de membro · ainda sem acesso à plataforma"}
              </p>
              {!selectedCareMember.hasAccess && (
                <p style={{ margin: "6px 0 0", color: "#F8DFA8", fontSize: 11, lineHeight: 1.5 }}>Ainda não criou conta no app, por isso não tem devocionais nem agendamentos pra contabilizar.</p>
              )}
              <div style={{ display: "grid", gridTemplateColumns: isTablet ? "1fr 1fr 1fr" : "1fr", gap: 8, marginTop: 10 }}>
                <div>
                  <p style={{ margin: "0 0 4px", color: "#8fb5cb", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 800 }}>Devocionais concluídos ({selectedCareMember.devotionalsCount})</p>
                  {selectedCareMember.devotionals.map((item, index) => (
                    <p key={index} style={{ margin: "0 0 2px", color: "#CBD5E1", fontSize: 11, overflowWrap: "anywhere" }}>{item.title}{item.trailTitle ? ` · ${item.trailTitle}` : ""} — {new Date(item.doneAt).toLocaleDateString("pt-BR")}</p>
                  ))}
                  {selectedCareMember.devotionals.length === 0 && <p style={{ margin: 0, color: "#7c8ba3", fontSize: 11 }}>Nenhum devocional concluído ainda.</p>}
                </div>
                <div>
                  <p style={{ margin: "0 0 4px", color: "#8fb5cb", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 800 }}>Leituras de ebook ({selectedCareMember.ebookReadsCount ?? 0})</p>
                  {(selectedCareMember.ebookReads || []).map((item, index) => (
                    <p key={index} style={{ margin: "0 0 2px", color: "#CBD5E1", fontSize: 11, overflowWrap: "anywhere" }}>{item.ebookTitle} · pág. {item.currentPage} — {new Date(item.updatedAt).toLocaleDateString("pt-BR")}</p>
                  ))}
                  {(!selectedCareMember.ebookReads || selectedCareMember.ebookReads.length === 0) && <p style={{ margin: 0, color: "#7c8ba3", fontSize: 11 }}>Nenhuma leitura de ebook registrada ainda.</p>}
                </div>
                <div>
                  <p style={{ margin: "0 0 4px", color: "#8fb5cb", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 800 }}>Conversas de acolhimento ({selectedCareMember.bookingsCount})</p>
                  {selectedCareMember.bookings.map((item, index) => (
                    <p key={index} style={{ margin: "0 0 2px", color: "#CBD5E1", fontSize: 11 }}>{new Date(item.startAt).toLocaleString("pt-BR")} com {item.pastorName || "acolhedor(a)"} · {item.status}</p>
                  ))}
                  {selectedCareMember.bookings.length === 0 && <p style={{ margin: 0, color: "#7c8ba3", fontSize: 11 }}>Nenhuma conversa registrada ainda.</p>}
                </div>
              </div>

              <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                <p style={{ margin: "0 0 6px", color: "#8fb5cb", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 800 }}>Convidar</p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  <button
                    onClick={() => {
                      const phoneDigits = (selectedCareMember.mobilePhone || "").replace(/\D/g, "");
                      if (!phoneDigits) {
                        setMemberInviteFeedback("Essa pessoa não tem WhatsApp cadastrado.");
                        return;
                      }
                      window.open(`https://wa.me/55${phoneDigits.replace(/^55/, "")}`, "_blank");
                    }}
                    style={{ ...buttonReset, minHeight: 34, padding: "0 12px", border: "1px solid rgba(148,163,184,0.3)", background: "rgba(148,163,184,0.08)", color: "#c7ced9", fontSize: 11, fontWeight: 900 }}
                  >
                    Abrir WhatsApp
                  </button>
                  {/* Cada botao mostra "ja em dd/mm" quando o mesmo tipo de
                      convite ja foi mandado antes (pedido do Rodrigo) -- fica
                      visivel sem bloquear o reenvio, pra quem realmente
                      precisar convidar de novo mais tarde. */}
                  {[
                    { type: "CAFE", label: "Convite pra um café", border: "rgba(251,191,36,0.4)", background: "rgba(251,191,36,0.12)", color: "#FDE68A" },
                    ...(!selectedCareMember.hasAccess ? [{ type: "SIGNUP", label: "Convite pra se cadastrar", border: "rgba(125,211,252,0.4)", background: "rgba(56,189,248,0.12)", color: "#BAE6FD" }] : []),
                    { type: "DEVOTIONAL", label: "Convite pra um devocional", border: "rgba(74,222,128,0.4)", background: "rgba(34,197,94,0.12)", color: "#86EFAC" }
                  ].map((item) => {
                    const previous = lastInviteOf(selectedCareMember, item.type);
                    return (
                      <button
                        key={item.type}
                        onClick={() => sendMemberInvite(item.type)}
                        disabled={sendingInviteType === item.type}
                        title={previous ? `Já convidado em ${new Date(previous.createdAt).toLocaleDateString("pt-BR")} -- clique pra convidar de novo` : undefined}
                        style={{
                          ...buttonReset,
                          minHeight: 34,
                          padding: "0 12px",
                          border: `1px solid ${previous ? "rgba(148,163,184,0.3)" : item.border}`,
                          background: previous ? "rgba(148,163,184,0.06)" : item.background,
                          color: previous ? "#9fb0cc" : item.color,
                          fontSize: 11,
                          fontWeight: 900,
                          opacity: sendingInviteType === item.type ? 0.6 : 1
                        }}
                      >
                        {item.label}{previous ? ` · já em ${new Date(previous.createdAt).toLocaleDateString("pt-BR")}` : ""}
                      </button>
                    );
                  })}
                </div>
                {memberInviteFeedback && <p style={{ margin: "6px 0 0", color: "#9fb0cc", fontSize: 11 }}>{memberInviteFeedback}</p>}
                {selectedCareMember.invites?.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    {selectedCareMember.invites.map((item) => (
                      <p key={item.id} style={{ margin: "0 0 2px", color: "#7c8ba3", fontSize: 10.5 }}>
                        {MEMBER_INVITE_LABELS[item.type] || item.type} · {new Date(item.createdAt).toLocaleDateString("pt-BR")}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div ref={trailInvitesSectionRef} style={{ ...cardStyle, marginBottom: 14, border: "1px solid rgba(148,163,184,0.16)", background: "rgba(255,255,255,0.015)", padding: "14px 16px" }}>
          {/* Pedido do Rodrigo (10/2026): o tile "Convites de trilha
              pendentes" so mostrava o numero, sem dizer quem convidou quem
              nem ha quanto tempo -- essa secao fecha essa lacuna. Convite de
              trilha e feito pelo proprio membro, pelo app (convidar um
              amigo/contato pra fazer uma trilha devocional junto),
              diferente dos convites de cafe/devocional/cadastro que a
              lideranca manda pra um membro aqui no painel. */}
          <p style={{ margin: "0 0 10px", color: "#7c8ba3", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 800 }}>Convites de trilha pendentes</p>
          {(!adminOverview?.pendingTrailInvitesList || adminOverview.pendingTrailInvitesList.length === 0) && (
            <p style={{ margin: 0, color: "#7c8ba3", fontSize: 11.5 }}>Nenhum convite pendente no momento.</p>
          )}
          {(adminOverview?.pendingTrailInvitesList || []).map((item) => {
            const daysAgo = Math.max(0, Math.floor((Date.now() - new Date(item.createdAt).getTime()) / 86400000));
            const channelLabel = item.channel === "whatsapp" ? "WhatsApp" : item.channel === "email" ? "e-mail" : item.channel;
            return (
              <div key={item.id} style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: 6, padding: "6px 0", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                <span style={{ color: "#E5E7EB", fontSize: 12, fontWeight: 800 }}>{item.inviteeName}</span>
                <span style={{ color: "#68758c", fontSize: 11 }}>
                  convidado(a) por {item.inviterName}{item.trailTitle ? ` · ${item.trailTitle}` : ""} · via {channelLabel} · há {daysAgo === 0 ? "menos de 1 dia" : `${daysAgo}d`}
                </span>
              </div>
            );
          })}
        </div>

        <div ref={bookingsSectionRef} style={{ ...cardStyle, border: "1px solid rgba(148,163,184,0.16)", background: "rgba(255,255,255,0.015)", padding: "14px 16px" }}>
          {/* Revisao de UX (10/2026, pedido do Rodrigo): "Conversas de
              acolhimento" e "Comunidade digital" eram 2 cards do mesmo
              tamanho que a busca de pessoa, competindo por atencao com o
              que ele realmente usa aqui. Agora sao uma unica tira de
              resumo, baixa densidade, com link pra quem quiser o detalhe --
              a lista completa de conversas ja existe na Agenda de cuidado,
              nao precisa duplicar aqui. "Como chegaram até a Mosaico"
              saiu de vez (Rodrigo confirmou que nao usa esse numero aqui). */}
          <p style={{ margin: "0 0 10px", color: "#7c8ba3", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 800 }}>Resumo (não é o foco desta tela)</p>

          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
            <span style={{ width: 150, flexShrink: 0, color: "#9fb0cc", fontSize: 11.5, fontWeight: 800 }}>Conversas de acolhimento</span>
            <div style={{ flex: 1, display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
              {recentBookingsList.length === 0 && <span style={{ color: "#7c8ba3", fontSize: 11 }}>Nenhuma registrada ainda.</span>}
              {pendingBookingsInRecent > 0 && (
                <span style={{ padding: "3px 9px", borderRadius: 999, fontSize: 10.5, fontWeight: 800, border: "1px solid rgba(250,204,21,0.4)", background: "rgba(250,204,21,0.12)", color: "#FDE68A" }}>
                  {pendingBookingsInRecent} pendente{pendingBookingsInRecent > 1 ? "s" : ""}
                </span>
              )}
              {confirmedBookingsInRecent > 0 && (
                <span style={{ padding: "3px 9px", borderRadius: 999, fontSize: 10.5, fontWeight: 800, border: "1px solid rgba(74,222,128,0.35)", background: "rgba(74,222,128,0.12)", color: "#86EFAC" }}>
                  {confirmedBookingsInRecent} confirmada{confirmedBookingsInRecent > 1 ? "s" : ""}
                </span>
              )}
            </div>
            <Link to="/familia?tab=cuidado" style={{ fontSize: 11, fontWeight: 900, color: "#7DD3FC", textDecoration: "none", whiteSpace: "nowrap" }}>Abrir agenda completa →</Link>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 10, padding: "8px 0", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
            <span style={{ width: 150, flexShrink: 0, color: "#9fb0cc", fontSize: 11.5, fontWeight: 800 }}>Devocional recente</span>
            <div style={{ flex: 1, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "baseline" }}>
              {(adminOverview?.recentDevotionalCompletions || []).slice(0, 5).map((item) => {
                const matchedMember = item.userId ? careDirectory.find((member) => member.linkedUser?.id === item.userId) : null;
                const Wrapper = matchedMember ? "button" : "span";
                return (
                  <Wrapper
                    key={item.id}
                    {...(matchedMember ? { type: "button", onClick: () => { setSelectedMemberId(matchedMember.id); scrollToSection(careSectionRef); } } : {})}
                    style={{ ...(matchedMember ? { ...buttonReset, cursor: "pointer" } : {}), fontSize: 11, color: matchedMember ? "#93C5FD" : "#CBD5E1", fontWeight: 800 }}
                  >
                    {item.userName}{matchedMember && "→"} <span style={{ color: "#68758c", fontWeight: 600 }}>{item.devotionalTitle}{item.trailTitle ? ` · ${item.trailTitle}` : ""} — {new Date(item.doneAt).toLocaleDateString("pt-BR")}</span>
                  </Wrapper>
                );
              })}
              {(!adminOverview?.recentDevotionalCompletions || !adminOverview.recentDevotionalCompletions.length) && <span style={{ color: "#7c8ba3", fontSize: 11 }}>Ninguém concluiu devocional ainda.</span>}
            </div>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 10, padding: "8px 0" }}>
            <span style={{ width: 150, flexShrink: 0, color: "#9fb0cc", fontSize: 11.5, fontWeight: 800 }}>Ebook recente</span>
            <div style={{ flex: 1, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "baseline" }}>
              {(adminOverview?.recentEbookReads || []).slice(0, 5).map((item) => {
                const matchedMember = item.userId ? careDirectory.find((member) => member.linkedUser?.id === item.userId) : null;
                const Wrapper = matchedMember ? "button" : "span";
                return (
                  <Wrapper
                    key={item.id}
                    {...(matchedMember ? { type: "button", onClick: () => { setSelectedMemberId(matchedMember.id); scrollToSection(careSectionRef); } } : {})}
                    style={{ ...(matchedMember ? { ...buttonReset, cursor: "pointer" } : {}), fontSize: 11, color: matchedMember ? "#93C5FD" : "#CBD5E1", fontWeight: 800 }}
                  >
                    {item.userName}{matchedMember && "→"} <span style={{ color: "#68758c", fontWeight: 600 }}>{item.ebookTitle} · pág. {item.currentPage} — {new Date(item.updatedAt).toLocaleDateString("pt-BR")}</span>
                  </Wrapper>
                );
              })}
              {(!adminOverview?.recentEbookReads || !adminOverview.recentEbookReads.length) && <span style={{ color: "#7c8ba3", fontSize: 11 }}>Ninguém leu ebook ainda.</span>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
